import React, { useState, useCallback, useRef, useEffect } from 'react';
import { MoreVertical, Download } from 'lucide-react';

interface ExportMenuProps {
  onExport: (format: string) => void;
}

const ExportMenu = React.memo(function ExportMenu({ onExport }: ExportMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleExport = useCallback(
    (format: string) => {
      onExport(format);
      setShowMenu(false);
      triggerRef.current?.focus();
    },
    [onExport]
  );

  const toggleMenu = useCallback(() => {
    setShowMenu(prev => !prev);
  }, []);

  // Focus first menu item when menu opens
  useEffect(() => {
    if (showMenu && menuRef.current) {
      const first = menuRef.current.querySelector<HTMLElement>('[role="menuitem"]');
      first?.focus();
    }
  }, [showMenu]);

  // Close on Escape, outside click, and arrow key navigation
  useEffect(() => {
    if (!showMenu) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMenu(false);
        triggerRef.current?.focus();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const menu = menuRef.current;
        if (!menu) return;
        const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'));
        if (items.length === 0) return;
        const current = items.indexOf(document.activeElement as HTMLElement);
        let next: number;
        if (e.key === 'ArrowDown') {
          next = current < items.length - 1 ? current + 1 : 0;
        } else {
          next = current > 0 ? current - 1 : items.length - 1;
        }
        items[next].focus();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        onClick={toggleMenu}
        type="button"
        className="p-1 rounded hover:opacity-80 focus:outline-2 focus:outline-blue-500"
        title="More options"
        aria-label="More options"
        aria-haspopup="true"
        aria-expanded={showMenu}
      >
        <MoreVertical className="w-4 h-4 text-gray-900" aria-hidden="true" />
      </button>
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute right-0 top-8 bg-white border rounded-lg shadow-lg py-1 min-w-[150px] z-50"
          role="menu"
          aria-label="Export options"
        >
          <button
            role="menuitem"
            tabIndex={-1}
            type="button"
            onClick={() => handleExport('json')}
            className="w-full text-left px-4 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 focus:outline-2 focus:outline-blue-500"
          >
            <Download className="w-3 h-3" aria-hidden="true" />
            Export as JSON
          </button>
          <button
            role="menuitem"
            tabIndex={-1}
            type="button"
            onClick={() => handleExport('txt')}
            className="w-full text-left px-4 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 focus:outline-2 focus:outline-blue-500"
          >
            <Download className="w-3 h-3" aria-hidden="true" />
            Export as Text
          </button>
          <button
            role="menuitem"
            tabIndex={-1}
            type="button"
            onClick={() => handleExport('md')}
            className="w-full text-left px-4 py-2 text-xs hover:bg-gray-50 flex items-center gap-2 focus:outline-2 focus:outline-blue-500"
          >
            <Download className="w-3 h-3" aria-hidden="true" />
            Export as Markdown
          </button>
        </div>
      )}
    </div>
  );
});

export default ExportMenu;
